import { color } from 'chart.js/helpers';
import { contextMenu } from 'react-contexify';

import createInfoboxMarkup from '../../../infobox/createInfoboxMarkup';
import {
  COMMON_CHART_FONT_STYLES,
  getChartBaseColors,
  getChartFontColor,
  getChartGridColor,
} from '../../../../utils/chart';
import { HORIZONTAL_BARS_CONTEXT_MENU } from './HorizontalBars';

export const createOptions = ({ theme, customTooltip, data, dataKey }) => {
  const fontColor = getChartFontColor(theme);
  const gridColor = getChartGridColor(theme);

  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    elements: {
      bar: {
        borderRadius: 5,
      },
    },
    interaction: {
      mode: 'index',
      axis: 'y',
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: fontColor,
          usePointStyle: true,
          boxWidth: 8,
          font: COMMON_CHART_FONT_STYLES,
          padding: 20,
        },
      },
      tooltip: {
        mode: 'index',
        axis: 'y',
        intersect: false,
        enabled: !customTooltip,
        external: function (context) {
          customTooltip && getTooltipsCustomConfig(context, { data, dataKey });
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        offset: true,
        position: 'top',
        ticks: {
          color: fontColor,
          font: COMMON_CHART_FONT_STYLES,
          padding: 8,
        },
        grid: {
          display: true,
          drawTicks: false,
          color: gridColor,
        },
      },
      y: {
        offset: true,
        position: 'left',
        ticks: {
          display: false,
        },
        grid: {
          display: true,
          drawTicks: false,
          color: gridColor,
        },
      },
    },
    events: ['mousemove', 'contextmenu', 'mouseout'],
    onClick: (event, elements) => {
      if (event.native.button === 2 && elements.length) {
        event.native.preventDefault();
        const clickedBar = elements[0];
        const index = clickedBar.index;
        const domain = data[index].domain;

        if (domain) {
          contextMenu.show({
            id: HORIZONTAL_BARS_CONTEXT_MENU,
            event: event.native,
            props: {
              domain,
            },
          });
        }
      }
    },
  };
};

export const createData = ({ data, theme, dataKey }) => {
  const labels = data.map((dto) => dto.label);
  const scores = data.map((dto) => dto[dataKey].score);
  const trends = data.map((dto) => dto[dataKey].trend);

  const BASE_COLORS = getChartBaseColors(theme);

  function getColor(value) {
    return value < 0 ? BASE_COLORS.RED : BASE_COLORS.GREEN;
  }

  return {
    labels,
    datasets: [
      {
        label: 'Score',
        backgroundColor: color(BASE_COLORS.BLUE).alpha(0.9).rgbString(),
        borderWidth: 0,
        minBarLength: 8,
        barThickness: 10,
        data: scores,
      },
      {
        label: 'Trend',
        backgroundColor: trends.map((value) => color(getColor(value)).alpha(0.9).rgbString()),
        borderWidth: 0,
        minBarLength: 8,
        barThickness: 10,
        data: trends,
      },
    ],
  };
};

const getTooltipsCustomConfig = (context, { data, dataKey }) => {
  const { chart, tooltip: tooltipModel } = context;

  // Tooltip Element
  let tooltipEl = document.getElementById('chartJsFancyColumnsNewTooltip');

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartJsFancyColumnsNewTooltip';
    document.body.appendChild(tooltipEl);
  }

  // Hide if no tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove('above', 'below', 'no-transform');
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add('no-transform');
  }

  // Set Content
  const index = tooltipModel.dataPoints[0].dataIndex;
  const label = tooltipModel.dataPoints[0].label;

  const domain = data[index].domain;
  const companyName = data[index].companyName || '';
  const colorScore = tooltipModel.labelColors[0].backgroundColor;
  const colorTrend = tooltipModel.labelColors[1].backgroundColor;

  const tooltipTitle = label;
  const tooltipSubtitle = companyName ? domain : '';

  if (tooltipModel.body) {
    tooltipEl.innerHTML = createInfoboxMarkup({
      title: tooltipTitle,
      subTitle: tooltipSubtitle,
      records: [
        { name: 'Score', value: data[index][dataKey].score, color: colorScore },
        { name: 'Trend', value: data[index][dataKey].trend, color: colorTrend },
      ],
    });
  }

  // Get the canvas bounds
  const canvasBounds = chart.canvas.getBoundingClientRect();

  // Calculate tooltip position
  const positionX = canvasBounds.left + window.scrollX;
  const positionY = canvasBounds.top + window.scrollY + tooltipModel.caretY;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.pointerEvents = 'none';
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.transform = 'translate(0, -50%)';
  tooltipEl.style.transition = 'all .5s ease';
  tooltipEl.style.left = positionX + 'px';
  tooltipEl.style.top = positionY + 'px';
  tooltipEl.style.font = tooltipModel.options.bodyFont.string;
  tooltipEl.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding + 'px';
};
